import { createBrowserRouter, Navigate } from 'react-router-dom';

import { ROUTES } from '@/constants';
import { AuthGuard } from '@/guards/AuthGuard';
import { AdminGuard } from '@/guards/AdminGuard';
import { VendorGuard } from '@/guards/VendorGuard';
import { VendorTypeGuard } from '@/guards/VendorTypeGuard';

// Layouts
import RootLayout from '@/components/layout/RootLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { VendorLayout } from '@/components/layout/VendorLayout';

// Pages (lazy loaded for code splitting)
import { lazy } from 'react';

const HomePage = lazy(() => import('@/pages/HomePage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
// Forgot/Reset password – disabled in UI; uncomment to re-enable
// const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
// const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const AdminDashboard = lazy(() =>
  import('@/pages/admin/AdminDashboard').then((module) => ({
    default: module.AdminDashboard,
  }))
);
const UserManagement = lazy(() =>
  import('@/pages/admin/UserManagement').then((module) => ({
    default: module.UserManagement,
  }))
);
const CreateUser = lazy(() =>
  import('@/pages/admin/CreateUser').then((module) => ({
    default: module.CreateUser,
  }))
);
const EditUser = lazy(() =>
  import('@/pages/admin/EditUser').then((module) => ({
    default: module.EditUser,
  }))
);
const AdminOrders = lazy(() => import('@/pages/admin/AdminOrders'));
const VendorEarnings = lazy(() => import('@/pages/admin/VendorEarnings'));
const CreateNotification = lazy(() =>
  import('@/pages/admin/CreateNotification').then((module) => ({
    default: module.CreateNotification,
  }))
);
const CreateCoupon = lazy(() =>
  import('@/pages/admin/CreateCoupon').then((module) => ({
    default: module.CreateCoupon,
  }))
);
const BannerManagement = lazy(() =>
  import('@/pages/admin/BannerManagement').then((module) => ({
    default: module.BannerManagement,
  }))
);
const SeedProducts = lazy(() =>
  import('@/pages/admin/SeedProducts').then((module) => ({
    default: module.SeedProducts,
  }))
);
const Security = lazy(() =>
  import('@/pages/admin/Security').then((module) => ({
    default: module.Security,
  }))
);
const Analytics = lazy(() =>
  import('@/pages/admin/Analytics').then((module) => ({
    default: module.Analytics,
  }))
);
const AdminSettings = lazy(() =>
  import('@/pages/admin/Settings').then((module) => ({
    default: module.Settings,
  }))
);
const VendorApprovals = lazy(() =>
  import('@/pages/admin/VendorApprovals').then((module) => ({
    default: module.VendorApprovals,
  }))
);
const ServiceCategories = lazy(() => import('@/pages/admin/ServiceCategories'));
const ProductCategories = lazy(() => import('@/pages/admin/ProductCategories'));
const AdminProducts = lazy(() => import('@/pages/admin/AdminProducts'));
const AdminCategoryVendors = lazy(() => import('@/pages/admin/AdminCategoryVendors'));
const AdminVendorCategoryRequests = lazy(() => import('@/pages/admin/AdminVendorCategoryRequests'));
const AdminServiceRequests = lazy(
  () => import('@/pages/admin/ServiceRequests')
);
const VendorDashboard = lazy(() =>
  import('@/pages/vendor/VendorDashboard').then((module) => ({
    default: module.VendorDashboard,
  }))
);
const ProductDashboard = lazy(() =>
  import('@/pages/vendor/ProductDashboard').then((module) => ({
    default: module.ProductDashboard,
  }))
);
const ProductManagement = lazy(() =>
  import('@/pages/vendor/ProductManagement').then((module) => ({
    default: module.ProductManagement,
  }))
);
const CreateProduct = lazy(() =>
  import('@/pages/vendor/CreateProduct').then((module) => ({
    default: module.CreateProduct,
  }))
);
const EditProduct = lazy(() =>
  import('@/pages/vendor/EditProduct').then((module) => ({
    default: module.EditProduct,
  }))
);
const ServiceDashboard = lazy(() =>
  import('@/pages/vendor/ServiceDashboard').then((module) => ({
    default: module.ServiceDashboard,
  }))
);
const ServiceManagement = lazy(() =>
  import('@/pages/vendor/ServiceManagement').then((module) => ({
    default: module.ServiceManagement,
  }))
);
const CreateService = lazy(() =>
  import('@/pages/vendor/CreateService').then((module) => ({
    default: module.CreateService,
  }))
);
const EditService = lazy(() =>
  import('@/pages/vendor/EditService').then((module) => ({
    default: module.EditService,
  }))
);
const AvailableServicesCatalog = lazy(
  () => import('@/pages/vendor/AvailableServicesCatalog')
);
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ProductList = lazy(() => import('@/pages/ProductList'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const ServiceList = lazy(() => import('@/pages/ServiceList'));
const ServiceDetail = lazy(() => import('@/pages/ServiceDetail'));
const CartPage = lazy(() => import('@/pages/CartPage'));
const WishlistPage = lazy(() => import('@/pages/WishlistPage'));
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'));
const OrderHistory = lazy(() => import('@/pages/OrderHistory'));
const OrderDetail = lazy(() => import('@/pages/OrderDetail'));
const VendorOrders = lazy(() =>
  import('@/pages/vendor/VendorOrders').then((module) => ({
    default: module.default,
  }))
);
const ServiceRequests = lazy(() => import('@/pages/vendor/ServiceRequests'));
const UserServiceRequests = lazy(() => import('@/pages/ServiceRequests'));
const ServiceRequestQuotes = lazy(() => import('@/pages/ServiceRequestQuotes'));
const BookService = lazy(() => import('@/pages/BookService'));
const BookServiceCategory = lazy(() => import('@/pages/BookServiceCategory'));
const BookServiceDetails = lazy(() => import('@/pages/BookServiceDetails'));
const AvailableRequests = lazy(
  () => import('@/pages/vendor/AvailableRequests')
);
const SubmitQuote = lazy(() => import('@/pages/vendor/SubmitQuote'));
const EditQuote = lazy(() => import('@/pages/vendor/EditQuote'));
const VendorSettings = lazy(() =>
  import('@/pages/vendor/VendorSettings').then((module) => ({
    default: module.VendorSettings,
  }))
);
const MyQuotes = lazy(() => import('@/pages/vendor/MyQuotes'));
const RequestProducts = lazy(() => import('@/pages/RequestProducts'));
const MyProductRequests = lazy(() => import('@/pages/MyProductRequests'));
const VendorProductRequests = lazy(() => import('@/pages/vendor/VendorProductRequests'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const SupportPage = lazy(() => import('@/pages/SupportPage'));
const AdminSupport = lazy(() =>
  import('@/pages/admin/AdminSupport').then((m) => ({ default: m.AdminSupport }))
);

/**
 * Application routes configuration
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: ROUTES.LOGIN,
        element: <LoginPage />,
      },
      {
        path: ROUTES.REGISTER,
        element: <RegisterPage />,
      },
      // Forgot/Reset password routes – disabled; uncomment lazy imports above and these blocks to re-enable
      // {
      //   path: ROUTES.FORGOT_PASSWORD,
      //   element: <ForgotPasswordPage />,
      // },
      // {
      //   path: ROUTES.RESET_PASSWORD,
      //   element: <ResetPasswordPage />,
      // },
      {
        path: ROUTES.DASHBOARD,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <DashboardPage />,
          },
          {
            path: 'products',
            element: <ProductList />,
          },
          {
            path: 'products/:productId',
            element: <ProductDetail />,
          },
          {
            path: 'profile',
            element: <ProfilePage />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
          {
            path: 'service-requests',
            element: <UserServiceRequests />,
          },
          {
            path: 'service-requests/:requestId/quotes',
            element: <ServiceRequestQuotes />,
          },
          {
            path: 'support',
            element: <SupportPage />,
          },
        ],
      },
      {
        path: '/products',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <ProductList />,
          },
          {
            path: ':productId',
            element: <ProductDetail />,
          },
          {
            path: 'request',
            element: <RequestProducts />,
          },
          {
            path: 'my-requests',
            element: <MyProductRequests />,
          },
        ],
      },
      {
        path: '/services',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.BOOK_SERVICE_CATEGORY} replace />,
          },
          {
            path: ':serviceId',
            element: <ServiceDetail />,
          },
        ],
      },
      {
        path: ROUTES.BOOK_SERVICE,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <BookService />,
          },
          {
            path: 'category',
            element: <BookServiceCategory />,
          },
          {
            path: 'details',
            element: <BookServiceDetails />,
          },
        ],
      },
      {
        path: ROUTES.CART,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <CartPage />,
          },
        ],
      },
      {
        path: ROUTES.PROFILE,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <ProfilePage />,
          },
        ],
      },
      {
        path: ROUTES.SETTINGS,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <SettingsPage />,
          },
        ],
      },
      {
        path: ROUTES.WISHLIST,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <WishlistPage />,
          },
        ],
      },
      {
        path: ROUTES.CHECKOUT,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <CheckoutPage />,
          },
        ],
      },
      {
        path: ROUTES.ORDERS,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <OrderHistory />,
          },
          {
            path: ':orderId',
            element: <OrderDetail />,
          },
        ],
      },
      {
        path: ROUTES.SUPPORT,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <SupportPage />,
          },
        ],
      },
      {
        path: ROUTES.ADMIN_DASHBOARD,
        element: (
          <AdminGuard>
            <AdminLayout />
          </AdminGuard>
        ),
        children: [
          {
            index: true,
            element: <AdminDashboard />,
          },
          {
            path: 'users',
            element: <UserManagement />,
          },
          {
            path: 'users/create',
            element: <CreateUser />,
          },
          {
            path: 'users/:userId/edit',
            element: <EditUser />,
          },
          {
            path: 'orders',
            element: <AdminOrders />,
          },
          {
            path: 'vendor-earnings',
            element: <VendorEarnings />,
          },
          {
            path: 'notifications/create',
            element: <CreateNotification />,
          },
          {
            path: 'coupons/create',
            element: <CreateCoupon />,
          },
          {
            path: 'banners',
            element: <BannerManagement />,
          },
          {
            path: 'seed-products',
            element: <SeedProducts />,
          },
          {
            path: 'security',
            element: <Security />,
          },
          {
            path: 'analytics',
            element: <Analytics />,
          },
          {
            path: 'settings',
            element: <AdminSettings />,
          },
          {
            path: 'approvals/vendors',
            element: <VendorApprovals />,
          },
          {
            path: 'service-categories/vendors',
            element: <AdminCategoryVendors />,
          },
          {
            path: 'service-categories',
            element: <ServiceCategories />,
          },
          {
            path: 'product-categories',
            element: <ProductCategories />,
          },
          {
            path: 'products',
            element: <AdminProducts />,
          },
          {
            path: 'vendor-category-requests',
            element: <AdminVendorCategoryRequests />,
          },
          {
            path: 'service-requests',
            element: <AdminServiceRequests />,
          },
          {
            path: 'support',
            element: <AdminSupport />,
          },
        ],
      },
      {
        path: ROUTES.VENDOR_DASHBOARD,
        element: (
          <VendorGuard>
            <VendorLayout />
          </VendorGuard>
        ),
        children: [
          {
            index: true,
            element: <VendorDashboard />,
          },
          {
            path: 'products',
            element: <Navigate to="/vendor" replace />,
          },
          {
            path: 'products/manage',
            element: (
              <VendorTypeGuard allow="products">
                <ProductManagement />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'products/create',
            element: (
              <VendorTypeGuard allow="products">
                <CreateProduct />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'products/:productId/edit',
            element: (
              <VendorTypeGuard allow="products">
                <EditProduct />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'services',
            element: <Navigate to="/vendor" replace />,
          },
          {
            path: 'services/catalog',
            element: (
              <VendorTypeGuard allow="services">
                <AvailableServicesCatalog />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'services/manage',
            element: (
              <VendorTypeGuard allow="services">
                <ServiceManagement />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'services/create',
            element: (
              <VendorTypeGuard allow="services">
                <CreateService />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'services/:serviceId/edit',
            element: (
              <VendorTypeGuard allow="services">
                <EditService />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'service-requests',
            element: (
              <VendorTypeGuard allow="services">
                <ServiceRequests />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'available-requests',
            element: (
              <VendorTypeGuard allow="services">
                <AvailableRequests />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'product-requests',
            element: (
              <VendorTypeGuard allow="products">
                <VendorProductRequests />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'requests/:requestId/quote',
            element: (
              <VendorTypeGuard allow="services">
                <SubmitQuote />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'quotes',
            element: (
              <VendorTypeGuard allow="services">
                <MyQuotes />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'quotes/:quoteId/edit',
            element: (
              <VendorTypeGuard allow="services">
                <EditQuote />
              </VendorTypeGuard>
            ),
          },
          {
            path: 'orders',
            element: <VendorOrders />,
          },
          {
            path: 'profile',
            element: <ProfilePage />,
          },
          {
            path: 'settings',
            element: <VendorSettings />,
          },
          {
            path: 'support',
            element: <SupportPage />,
          },
        ],
      },
      {
        path: ROUTES.NOT_FOUND,
        element: <NotFoundPage />,
      },
      {
        path: '*',
        element: <Navigate to={ROUTES.NOT_FOUND} replace />,
      },
    ],
  },
]);
